import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { TableTools } from '../../components/ui/TableTools';
import { num } from '../../format';
import { useAuth } from '../auth/AuthContext';
import { downloadBlob } from '../labels/api';
import { useNavigation } from '../layout/navigation';
import { createDrafts, type WantedLine } from '../purchases/api';
import { lowStock, lowStockXlsx, type LowStockLine } from './api';

/** How a supplier is keyed while grouping. Null is a real answer — "nobody has
 *  sold us this yet" — and it has to survive being used as a map key. */
const NONE = ' none';

/**
 * What is running out, and what to do about it.
 *
 * Built like the shortage report next door, because the two answer the same
 * question from different directions and a person who has learned one should not
 * have to learn the other. A minimum level is a standing opinion about a part;
 * a shortage is arithmetic over what is planned. Both end in an order.
 *
 * The quantity offered is what it takes to reach the minimum again, not the
 * whole minimum: the shelf already holds some of it.
 */
export function LowStockPanel() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { open } = useNavigation();

  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<{ code: string; id: string }[]>([]);

  const rows = useQuery({ queryKey: ['stock', 'low'], queryFn: lowStock });
  const canBuy = can('purchase.write');

  const list = useMemo(() => rows.data ?? [], [rows.data]);

  const toggle = (id: string) =>
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selected = useMemo(
    () => list.filter((row) => chosen.has(row.component.id)),
    [list, chosen],
  );

  const vendorCount = useMemo(
    () => new Set(selected.map((row) => row.vendor_id ?? NONE)).size,
    [selected],
  );

  const raise = useMutation({
    mutationFn: () => {
      const lines: WantedLine[] = selected.map((row) => ({
        component_id: row.component.id,
        // Never zero: a part sitting exactly on its minimum is still on the
        // list, and an order for none of it is not an order.
        quantity: Number(row.shortfall) > 0 ? row.shortfall : '1',
        vendor_id: row.vendor_id,
        purpose_note: t('stock.low.note'),
      }));
      return createDrafts(lines, t('stock.low.note'));
    },
    onSuccess: (purchases) => {
      setMade(purchases.map((row) => ({ code: row.code, id: row.id })));
      setChosen(new Set());
      setError(null);
    },
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  const line = (row: LowStockLine) => (
    <tr key={row.component.id} className={chosen.has(row.component.id) ? 'is-chosen' : undefined}>
      {canBuy && (
        <td>
          <input
            type="checkbox"
            checked={chosen.has(row.component.id)}
            onChange={() => toggle(row.component.id)}
            aria-label={row.component.name}
          />
        </td>
      )}
      <td>
        <button type="button" className="link" onClick={() => open('component', row.component.id)}>
          {row.component.name}
        </button>
        <span className="components__code"> {row.component.internal_code}</span>
      </td>
      <td>
        <strong className={Number(row.qty_on_hand) <= 0 ? 'stock__negative' : undefined}>
          {num(row.qty_on_hand)}
        </strong>
      </td>
      <td>{num(row.min_stock)}</td>
      <td>
        <strong className="stock__negative">{num(row.shortfall)}</strong>
      </td>
      <td>{row.vendor_name ?? <em className="hint">{t('stock.shortage.noVendor')}</em>}</td>
    </tr>
  );

  return (
    <section className="shortage">
      <div className="panel__section-head">
        <h3>{t('stock.lowStock')}</h3>
        <TableTools
          label={t('stock.lowStock')}
          onExport={() => void lowStockXlsx().then((blob) => downloadBlob(blob, 'low-stock.xlsx'))}
        />
      </div>

      {rows.isLoading && <p className="panel__hint">{t('common.loading')}</p>}
      {rows.data?.length === 0 && <p className="panel__hint">{t('stock.lowStockEmpty')}</p>}

      {made.length > 0 && (
        <p className="stock__ok">
          {t('stock.shortage.created', { n: made.length })}{' '}
          {made.map((row) => (
            <button
              key={row.id}
              type="button"
              className="link"
              onClick={() => open('purchase', row.id)}
            >
              {row.code}
            </button>
          ))}
        </p>
      )}
      {error && <p className="panel__error">{t(`stock.shortage.errors.${error}`, error)}</p>}

      {list.length > 0 && (
        <>
          <div className="scroll-x scroll-y">
            <table className="shortage__grid">
              <thead>
                <tr>
                  {canBuy && <th aria-label={t('stock.shortage.choose')} />}
                  <th>{t('stock.table.column.name')}</th>
                  <th>{t('stock.table.column.qty')}</th>
                  <th>{t('stock.minimum')}</th>
                  <th>{t('stock.low.buy')}</th>
                  <th>{t('stock.shortage.vendor')}</th>
                </tr>
              </thead>
              <tbody>{list.map(line)}</tbody>
            </table>
          </div>

          {canBuy && (
            <div className="shortage__actions">
              <button
                type="button"
                className="btn btn--primary"
                disabled={selected.length === 0 || raise.isPending}
                onClick={() => raise.mutate()}
              >
                {t('stock.shortage.raise', { rows: selected.length, orders: vendorCount })}
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => setChosen(new Set(list.map((row) => row.component.id)))}
              >
                {t('stock.shortage.chooseAll')}
              </button>
              <span className="hint">{t('stock.shortage.priceHint')}</span>
            </div>
          )}
        </>
      )}
    </section>
  );
}
